import React, { RefObject, useEffect, useRef, useState } from "react";

import { Box } from "@mui/material";
import { Theme, alpha, useTheme } from "@mui/material/styles";
import { CSSObject } from "@mui/system";

import {
  actionsCellSx,
  bodyCellBaseSx
} from "@/src/components/organisms/AdminDataTable/adminDataTable.styles";
import {
  Cell,
  Row,
  Table,
  flexRender
} from "@/src/components/organisms/AdminDataTable/reactTable";

import { useVirtualizer } from "@tanstack/react-virtual";

interface AdminDataTableBodyProps<TData extends object> {
  table: Table<TData>;
  rowHeight: number;
  scrollRef: RefObject<HTMLDivElement | null>;
  columnCount: number;
  scrollToRowId?: string;
  onScrolledToRow?: () => void;
}

const HIGHLIGHT_DURATION_MS = 3000;

const getCellPinningSx = <TData extends object>(
  cell: Cell<TData, unknown>,
  theme: Theme
): CSSObject => {
  const pinned = cell.column.getIsPinned();
  if (!pinned) return {};
  const pinShadow = `1px 0 0 0 ${theme.palette.action.selected}`;
  if (pinned === "start") {
    return {
      position: "sticky",
      left: cell.column.getStart("start"),
      zIndex: 2,
      backgroundColor: theme.palette.background.paper,
      boxShadow: pinShadow
    };
  }
  return {
    position: "sticky",
    right: cell.column.getAfter("end"),
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    boxShadow: `-1px 0 0 0 ${theme.palette.action.selected}`
  };
};

const AdminDataTableBody = <TData extends object>(
  props: AdminDataTableBodyProps<TData>
) => {
  const {
    table,
    rowHeight,
    scrollRef,
    columnCount,
    scrollToRowId,
    onScrolledToRow
  } = props;
  const theme = useTheme();
  const rows = table.getRowModel().rows;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 10
  });

  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const scrolledForIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!scrollToRowId) return;
    if (scrolledForIdRef.current === scrollToRowId) return;
    const index = rows.findIndex((row) => row.id === scrollToRowId);
    if (index === -1) return;

    scrolledForIdRef.current = scrollToRowId;
    virtualizer.scrollToIndex(index, { align: "center" });
    setHighlightedRowId(scrollToRowId);
    onScrolledToRow?.();

    requestAnimationFrame(() => {
      scrollRef.current
        ?.querySelector<HTMLElement>(`[data-index="${index}"]`)
        ?.scrollIntoView({ block: "center" });
    });
  }, [scrollToRowId, rows, virtualizer, onScrolledToRow, scrollRef]);

  useEffect(() => {
    if (!scrollToRowId) scrolledForIdRef.current = null;
  }, [scrollToRowId]);

  useEffect(() => {
    if (!highlightedRowId) return;
    const timeout = globalThis.setTimeout(
      () => setHighlightedRowId(null),
      HIGHLIGHT_DURATION_MS
    );
    return () => globalThis.clearTimeout(timeout);
  }, [highlightedRowId]);

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? totalSize - virtualItems[virtualItems.length - 1].end
      : 0;

  return (
    <Box component="tbody">
      {paddingTop > 0 && (
        <Box component="tr" aria-hidden="true">
          <Box
            component="td"
            colSpan={columnCount}
            style={{ height: paddingTop }}
          />
        </Box>
      )}
      {virtualItems.map((virtualRow) => {
        const row = rows[virtualRow.index] as Row<TData>;
        const isHighlighted = row.id === highlightedRowId;
        return (
          <Box
            component="tr"
            key={row.id}
            data-index={virtualRow.index}
            style={{ height: rowHeight }}
            sx={{
              "&:hover td[data-pinned='false']": {
                backgroundColor: isHighlighted
                  ? theme.palette.action.selected
                  : theme.palette.action.hover
              }
            }}
          >
            {row.getVisibleCells().map((cell) => {
              const meta = cell.column.columnDef.meta as
                | { isActionsCell?: boolean }
                | undefined;
              const isActions = meta?.isActionsCell === true;
              const isPinned = Boolean(cell.column.getIsPinned());
              return (
                <Box
                  component="td"
                  key={cell.id}
                  data-pinned={isPinned}
                  style={{ width: cell.column.getSize() }}
                  sx={[
                    bodyCellBaseSx,
                    getCellPinningSx(cell, theme),
                    isActions ? actionsCellSx : {},
                    {
                      height: rowHeight,
                      transition: isHighlighted
                        ? "none"
                        : "background-color 2s ease"
                    },
                    isHighlighted && !isPinned
                      ? {
                          backgroundColor: alpha(
                            theme.palette.warning.main,
                            0.3
                          )
                        }
                      : {}
                  ]}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Box>
              );
            })}
          </Box>
        );
      })}
      {paddingBottom > 0 && (
        <Box component="tr" aria-hidden="true">
          <Box
            component="td"
            colSpan={columnCount}
            style={{ height: paddingBottom }}
          />
        </Box>
      )}
    </Box>
  );
};

export default AdminDataTableBody;
